import { useEffect, useRef, useState, useContext, useCallback } from 'react';
import { io } from 'socket.io-client';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import { UserContext, SnackContext } from 'src/store/ContextStore';

const HEARTBEAT_INTERVAL_MS = 30_000; // eslint-disable-line no-unused-vars

/**
 * Manages person-level locks for a pedigree.
 *
 * Returns a hook object with:
 *   lockedPersons        Map<personId, lockInfo>  — all active locks for this pedigree
 *   isLockedByOther(pid) boolean — true if the person is locked by someone else
 *   isMine(pid)          boolean — true if the current user holds the lock
 *   acquireLock(pid)     async → { success, conflict? }
 *   releaseLock(pid)     async → void
 *   requestLock(pid)     → void  (sends access request to holder)
 *   forceAcquireLock(pid) async → void
 *   acquireBatch(pids, pedigreeId) async → { success, conflicts? }
 *   releaseBatch(pids, pedigreeId) async → void
 */
export function usePersonLocks(familyId, options = {}) {
  // options.active (default true) — only the visible/active tab keeps a live
  // person-lock socket. This socket only OBSERVES (its teardown releases nothing),
  // so a background tab can safely drop it and re-hydrate on activation.
  const { active = true } = options;
  const { user } = useContext(UserContext);
  const { setSnack } = useContext(SnackContext);

  // Map<personId, lockInfo>
  const [lockedPersons, setLockedPersons] = useState(new Map());
  // Map<personId, 'pending'|'granted'|'denied'>
  const [requestStatuses, setRequestStatuses] = useState(new Map());
  // Persistent banner state when someone requests access to a person we hold
  // null | { requesterName, requesterId, personId }
  const [pendingPersonRequest, setPendingPersonRequest] = useState(null);

  const socketRef = useRef(null);
  const sessionId = useRef(String(Date.now() + Math.random()));

  const userId = user?._id ? String(user._id) : null;

  const isLockedByOther = useCallback(
    (personId) => {
      if (!personId) return false;
      const lock = lockedPersons.get(personId);
      if (!lock) return false;
      return String(lock.lockedByUserId) !== userId;
    },
    [lockedPersons, userId]
  );

  const isMine = useCallback(
    (personId) => {
      if (!personId) return false;
      const lock = lockedPersons.get(personId);
      if (!lock) return false;
      return String(lock.lockedByUserId) === userId;
    },
    [lockedPersons, userId]
  );

  // Set up socket connection and listen for lock events
  useEffect(() => {
    if (!familyId || !active) return;

    const socket = io('', { transports: ['websocket', 'polling'] });
    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('pedigree:join', { familyId });
    });

    // Hydrate all current locks on join
    socket.on('person:locks-batch', ({ locks }) => {
      setLockedPersons((prev) => {
        const next = new Map(prev);
        locks.forEach(({ personId, lock }) => {
          next.set(personId, lock);
        });
        return next;
      });
    });

    // A single person lock was acquired
    socket.on('person:lock-acquired', ({ personId, lock }) => {
      setLockedPersons((prev) => new Map(prev).set(personId, lock));
    });

    // A single person lock was released
    socket.on('person:lock-released', ({ personId }) => {
      setLockedPersons((prev) => {
        const next = new Map(prev);
        next.delete(personId);
        return next;
      });
      setPendingPersonRequest((prev) =>
        prev?.personId === personId ? null : prev
      );
      // Clear pending request status so the requester's UI updates
      setRequestStatuses((prev) => {
        const next = new Map(prev);
        next.delete(personId);
        return next;
      });
    });

    // Batch release
    socket.on('person:locks-released', ({ personIds }) => {
      setLockedPersons((prev) => {
        const next = new Map(prev);
        personIds.forEach((pid) => next.delete(pid));
        return next;
      });
      setRequestStatuses((prev) => {
        const next = new Map(prev);
        personIds.forEach((pid) => next.delete(pid));
        return next;
      });
    });

    // Force acquire
    socket.on('person:lock-force-acquired', ({ personId, lock }) => {
      setLockedPersons((prev) => new Map(prev).set(personId, lock));
      setPendingPersonRequest((prev) =>
        prev?.personId === personId ? null : prev
      );
      // Someone else took the lock — clear any pending request we had for it
      setRequestStatuses((prev) => {
        const next = new Map(prev);
        next.delete(personId);
        return next;
      });
    });

    // Someone is requesting access to a person we hold — show persistent banner
    socket.on(
      'person:lock-request-received',
      ({ requesterName, requesterId, personId, targetUserId }) => {
        if (targetUserId && userId && String(targetUserId) !== userId) return;
        setPendingPersonRequest({ requesterName, requesterId, personId });
      }
    );

    // Requester cancelled their person lock request — clear our banner
    socket.on('person:lock-request-cancelled', ({ personId, requesterId }) => {
      setPendingPersonRequest((prev) =>
        prev?.personId === personId &&
        String(prev.requesterId) === String(requesterId)
          ? null
          : prev
      );
    });

    // Response to our own access request
    socket.on('person:lock-request-result', ({ personId, granted }) => {
      setRequestStatuses((prev) => {
        const next = new Map(prev);
        next.set(personId, granted ? 'granted' : 'denied');
        return next;
      });
      if (!granted) {
        setSnack({
          status: true,
          severity: 'warning',
          msg: 'Your request for edit access was declined.'
        });
      }
    });

    return () => {
      socket.emit('pedigree:leave', { familyId });
      socket.disconnect();
      socketRef.current = null;
    };
  }, [familyId, active]); // eslint-disable-line react-hooks/exhaustive-deps

  const acquireLock = useCallback(
    async (personId) => {
      try {
        const res = await axios.post(
          `${APICONSTANTS.personLock}/${personId}/acquire`,
          {
            pedigreeId: familyId,
            sessionId: socketRef.current?.id || sessionId.current
          }
        );
        if (res.data.acquired) {
          setLockedPersons((prev) =>
            new Map(prev).set(personId, res.data.lock)
          );
          return { success: true };
        }
        // Acquire failed — populate lockedPersons immediately so lockedByOther
        // is true without waiting for the socket event
        if (res.data.lock) {
          setLockedPersons((prev) =>
            new Map(prev).set(personId, res.data.lock)
          );
        }
        return { success: false, conflict: res.data.lock };
      } catch (err) {
        if (err.response?.status === 409) {
          const conflictLock = err.response.data.lock;
          if (conflictLock) {
            setLockedPersons((prev) =>
              new Map(prev).set(personId, conflictLock)
            );
          }
          return { success: false, conflict: conflictLock };
        }
        console.error('Failed to acquire person lock', err);
        return { success: false };
      }
    },
    [familyId]
  );

  const releaseLock = useCallback(
    async (personId) => {
      try {
        await axios.post(`${APICONSTANTS.personLock}/${personId}/release`, {
          pedigreeId: familyId
        });
        setLockedPersons((prev) => {
          const next = new Map(prev);
          next.delete(personId);
          return next;
        });
      } catch (err) {
        console.error('Failed to release person lock', err);
      }
    },
    [familyId]
  );

  const sendHeartbeat = useCallback(async (personId) => {
    try {
      await axios.post(`${APICONSTANTS.personLock}/${personId}/heartbeat`);
    } catch (_) {
      // ignore
    }
  }, []);

  const requestLock = useCallback(
    (personId) => {
      const lock = lockedPersons.get(personId);
      if (!lock) return;
      setRequestStatuses((prev) => new Map(prev).set(personId, 'pending'));
      axios
        .post(`${APICONSTANTS.personLock}/${personId}/request`, {
          pedigreeId: familyId,
          lockHolderUserId: lock.lockedByUserId
        })
        .catch(() =>
          setRequestStatuses((prev) => {
            const next = new Map(prev);
            next.delete(personId);
            return next;
          })
        );
    },
    [familyId, lockedPersons]
  );

  const cancelRequestLock = useCallback(
    (personId) => {
      const lock = lockedPersons.get(personId);
      setRequestStatuses((prev) => {
        const next = new Map(prev);
        next.delete(personId);
        return next;
      });
      const socket = socketRef.current;
      if (socket && lock) {
        socket.emit('person:lock-request-cancel', {
          pedigreeId: familyId,
          personId,
          lockHolderUserId: lock.lockedByUserId
        });
      }
    },
    [familyId, lockedPersons]
  );

  const dismissPersonRequest = useCallback(() => {
    setPendingPersonRequest(null);
  }, []);

  const forceAcquireLock = useCallback(
    async (personId) => {
      try {
        const res = await axios.post(
          `${APICONSTANTS.personLock}/${personId}/force-acquire`,
          {
            pedigreeId: familyId,
            sessionId: socketRef.current?.id || sessionId.current
          }
        );
        if (res.data.acquired) {
          setLockedPersons((prev) =>
            new Map(prev).set(personId, res.data.lock)
          );
        }
      } catch (err) {
        console.error('Failed to force-acquire person lock', err);
      }
    },
    [familyId]
  );

  const acquireBatch = useCallback(
    async (personIds) => {
      try {
        const res = await axios.post(
          `${APICONSTANTS.personLockBatch}/acquire`,
          {
            personIds,
            pedigreeId: familyId,
            sessionId: socketRef.current?.id || sessionId.current
          }
        );
        if (res.data.acquired) {
          setLockedPersons((prev) => {
            const next = new Map(prev);
            res.data.locks.forEach((l) => next.set(l.personId, l));
            return next;
          });
          return { success: true };
        }
        return { success: false, conflicts: res.data.conflicts };
      } catch (err) {
        if (err.response?.status === 409) {
          return {
            success: false,
            conflicts: err.response.data.conflicts
          };
        }
        console.error('Failed to acquire batch person locks', err);
        return { success: false, conflicts: [] };
      }
    },
    [familyId]
  );

  const releaseBatch = useCallback(
    async (personIds) => {
      try {
        await axios.post(`${APICONSTANTS.personLockBatch}/release`, {
          personIds,
          pedigreeId: familyId
        });
        setLockedPersons((prev) => {
          const next = new Map(prev);
          personIds.forEach((pid) => next.delete(pid));
          return next;
        });
      } catch (err) {
        console.error('Failed to release batch person locks', err);
      }
    },
    [familyId]
  );

  return {
    lockedPersons,
    isLockedByOther,
    isMine,
    requestStatuses,
    pendingPersonRequest,
    dismissPersonRequest,
    acquireLock,
    releaseLock,
    sendHeartbeat,
    requestLock,
    cancelRequestLock,
    forceAcquireLock,
    acquireBatch,
    releaseBatch
  };
}
